import { SupplementaryInsuranceTypes } from '../../../states/offer/offer.state';
import { Item } from './tracking-ecommerce.events';
import {
  CATEGORY,
  CATEGORY_ID,
  CURRENCY_CHF,
  genderNames,
  mapContractLength,
  mapCustomerType,
} from './tracking-ecommerce.map';
import {
  BasicOfferType,
  Complementary,
  ContractDuration,
  GenderOrPrenatal,
  HospitalOfferType,
  HospitalVivaOfferType,
  Level,
  Outpatient,
  OutpatientViva,
} from '../../../api';
import { isNotNullOrUndefined } from '../../../utils/typeUtils';
import {
  HospitalOrBasicInsurance,
  SupplementaryInsurancesData,
} from '../../../../pages/supplementary-insurance/supplementary-insurance-selection-page/supplementary-insurance.mapper';

const supplementaryInsuranceIds: {
  [key in SupplementaryInsuranceTypes]: string;
} = {
  basic: 'basic',
  hospital: 'hospital',
  hospitalViva: 'hospital_viva',
  complementary: 'complementary',
  complementaryViva: 'complementary_viva',
  outpatient: 'outpatient',
  outpatientViva: 'outpatient_viva',
  legalProtection: 'legal_protection',
  hospitalCapital: 'hospital_capital',
  accidentDeathCapital: 'accident_death_capital',
  accidentDisabilityCapital: 'accident_disability_capital',
  sicknessDeathOrDisabilityCapital: 'sickness_death_or_disability_capital',
  dental: 'dental',
  hospitalDailyCash: 'hospital_daily_cash',
  dailyCash: 'daily_cash',
  emergencyCare: 'emergency_care',
  accidentMedicalExpensesSupplement: 'accident_medical_expenses_supplement',
};

const supplementaryInsuranceNames: {
  [key in SupplementaryInsuranceTypes]: string;
} = {
  basic: 'Basic',
  hospital: 'Spital',
  complementary: 'Komplementär',
  outpatient: 'Ambulant',
  legalProtection: 'Rechtsschutz',
  hospitalCapital: 'Spitalkapital',
  accidentDeathCapital: 'Todesfallkapital bei Unfall',
  accidentDisabilityCapital: 'Invaliditätskapital bei Unfall',
  sicknessDeathOrDisabilityCapital: 'Kapitalversicherung infolge Krankheit',
  dental: 'Zahnbehandlung',
  hospitalDailyCash: 'Spitaltaggeld',
  dailyCash: 'Taggeld',
  emergencyCare: 'Notfallbetreuung',
  accidentMedicalExpensesSupplement: 'Heilungskostenzusatz bei Unfall',
  hospitalViva: 'Spital VIVA',
  complementaryViva: 'Komplementär VIVA',
  outpatientViva: 'Ambulant VIVA',
};

const basicId: {
  [key in BasicOfferType]: string;
} = {
  BASIC_GENERAL: 'basic_general',
  BASIC_FLEX_24: 'basic_flex_24',
  BASIC_FLEX_48: 'basic_flex_48',
  BASIC_SEMI_PRIVATE: 'basic_semi_private',
  BASIC_PRIVATE: 'basic_private',
};

const outpatientId: {
  [key in Level]: string;
} = {
  LEVEL_ONE: 'outpatient_level_one',
  LEVEL_TWO: 'outpatient_level_two',
  LEVEL_THREE: 'outpatient_level_three',
};

const outpatientVivaId: {
  [key in Level]: string;
} = {
  LEVEL_ONE: 'outpatient_viva_level_one',
  LEVEL_TWO: 'outpatient_viva_level_two',
  LEVEL_THREE: 'outpatient_viva_level_three',
};

const complementaryId: {
  [key in Level]: string;
} = {
  LEVEL_ONE: 'complementary_level_one',
  LEVEL_TWO: 'complementary_level_two',
  LEVEL_THREE: 'complementary_level_three',
};

const hospitalId: {
  [key in HospitalOfferType]: string;
} = {
  HOSPITAL_GENERAL: 'hospital_general',
  HOSPITAL_FLEX_PLUS: 'hospital_flex_plus',
  HOSPITAL_FLEX_48: 'hospital_flex_48',
  HOSPITAL_FLEX_24: 'hospital_flex_24',
  HOSPITAL_SEMI_PRIVATE: 'hospital_semi_private',
  HOSPITAL_PRIVATE: 'hospital_private',
};

const hospitalVivaId: {
  [key in HospitalVivaOfferType]: string;
} = {
  HOSPITAL_VIVA_GENERAL: 'hospital_viva_general',
  HOSPITAL_VIVA_FLEX_48: 'hospital_viva_flex_48',
  HOSPITAL_VIVA_FLEX_24: 'hospital_viva_flex_24',
  HOSPITAL_VIVA_SEMI_PRIVATE: 'hospital_viva_semi_private',
  HOSPITAL_VIVA_PRIVATE: 'hospital_viva_private',
};

const basicVariant: {
  [key in BasicOfferType]: string;
} = {
  BASIC_GENERAL: 'Basic Allgemein',
  BASIC_FLEX_24: 'Basic Flex 24',
  BASIC_FLEX_48: 'Basic Flex 48',
  BASIC_SEMI_PRIVATE: 'Basic Halbprivat',
  BASIC_PRIVATE: 'Basic Privat',
};

const hospitalVariant: {
  [key in HospitalOfferType]: string;
} = {
  HOSPITAL_GENERAL: 'Hospital General',
  HOSPITAL_FLEX_PLUS: 'Hospital Flex Plus',
  HOSPITAL_FLEX_48: 'Hospital Flex 48',
  HOSPITAL_FLEX_24: 'Hospital Flex 24',
  HOSPITAL_SEMI_PRIVATE: 'Hospital Semi Private',
  HOSPITAL_PRIVATE: 'Hospital Private',
};

const hospitalVivaVariant: {
  [key in HospitalVivaOfferType]: string;
} = {
  HOSPITAL_VIVA_GENERAL: 'hospital_viva_general',
  HOSPITAL_VIVA_FLEX_48: 'hospital_viva_flex_48',
  HOSPITAL_VIVA_FLEX_24: 'hospital_viva_flex_24',
  HOSPITAL_VIVA_SEMI_PRIVATE: 'hospital_viva_semi_private',
  HOSPITAL_VIVA_PRIVATE: 'hospital_viva_private',
};

const outpatientVariant: {
  [key in Level]: string;
} = {
  LEVEL_ONE: 'Ambulant level 1',
  LEVEL_TWO: 'Ambulant level 2',
  LEVEL_THREE: 'Ambulant level 3',
};

const complementaryVariant: {
  [key in Level]: string;
} = {
  LEVEL_ONE: 'Komplementär level 1',
  LEVEL_TWO: 'Komplementär level 2',
  LEVEL_THREE: 'Komplementär level 3',
};

export function mapSupplementaryInsuranceItem(product: SupplementaryInsuranceTypes, price: number): Item {
  return {
    item_id: supplementaryInsuranceIds[product],
    item_name: supplementaryInsuranceNames[product],
    currency: CURRENCY_CHF,
    index: 1,
    item_category: CATEGORY.HEALTH_INSURANCE,
    item_category2: CATEGORY.SUPPLEMENTARY_INSURANCE,
    item_list_id: CATEGORY_ID.SUPPLEMENTARY_INSURANCE,
    item_list_name: CATEGORY_ID.SUPPLEMENTARY_INSURANCE,
    price,
    quantity: 1,
  };
}

export function mapSupplementaryInsuranceVariantItem(product: SupplementaryInsuranceTypes, price: number): Item {
  return mapItem(
    supplementaryInsuranceIds[product],
    supplementaryInsuranceNames[product],
    1,
    supplementaryInsuranceNames[product],
    price,
  );
}

export function mapComplementaryVariantItem(complementary: Complementary, index: number): Item {
  return mapItem(
    complementaryId[complementary.level],
    supplementaryInsuranceNames['complementary'],
    index,
    complementaryVariant[complementary.level],
    complementary.price,
  );
}

export function mapOutpatientVariantItem(outpatient: Outpatient, index: number): Item {
  return mapItem(
    outpatientId[outpatient.level],
    supplementaryInsuranceNames['outpatient'],
    index,
    outpatientVariant[outpatient.level],
    outpatient.price,
  );
}

export function mapOutpatientVivaVariantItem(outpatient: OutpatientViva, index: number): Item {
  return mapItem(
    outpatientVivaId[outpatient.level],
    supplementaryInsuranceNames['outpatientViva'],
    index,
    outpatientVariant[outpatient.level],
    outpatient.price,
  );
}

export function mapHospitalVariantItemOrUndefined(
  price: number | undefined,
  offerType: HospitalOfferType,
): Item | undefined {
  return isNotNullOrUndefined(price) && price !== Infinity
    ? mapItem(hospitalId[offerType], supplementaryInsuranceNames['hospital'], 0, hospitalVariant[offerType], price)
    : undefined;
}

export function mapHospitalVivaVariantItemOrUndefined(
  price: number | undefined,
  offerType: HospitalVivaOfferType,
): Item | undefined {
  return isNotNullOrUndefined(price) && price !== Infinity
    ? mapItem(
        hospitalVivaId[offerType],
        supplementaryInsuranceNames['hospitalViva'],
        0,
        hospitalVivaVariant[offerType],
        price,
      )
    : undefined;
}

export function mapBasicVariantItemOrUndefined(price: number | undefined, offerType: BasicOfferType): Item | undefined {
  return isNotNullOrUndefined(price) && price !== Infinity
    ? mapItem(basicId[offerType], supplementaryInsuranceNames['basic'], 0, basicVariant[offerType], price)
    : undefined;
}

function mapItem(itemId: string, itemName: string, index: number, itemVariant: string, price: number): Item {
  return {
    item_id: itemId,
    item_name: itemName,
    currency: CURRENCY_CHF,
    index: index,
    item_category: CATEGORY.HEALTH_INSURANCE,
    item_category2: CATEGORY.SUPPLEMENTARY_INSURANCE,
    item_list_id: CATEGORY_ID.SUPPLEMENTARY_INSURANCE,
    item_list_name: CATEGORY_ID.SUPPLEMENTARY_INSURANCE,
    item_variant: itemVariant,
    price: price,
    quantity: 1,
  };
}

export function mapSupplementaryInsuranceItems(supplementaryInsuranceData: SupplementaryInsurancesData): Item[] {
  const filteredEntries = Object.entries(supplementaryInsuranceData)
    .filter(([_, value]) => isNaN(Number(value)))
    .map(([key, _]) => {
      const insuranceType = key as SupplementaryInsuranceTypes;
      return mapSupplementaryInsuranceItemOrUndefined(
        insuranceType,
        supplementaryInsuranceData[`${insuranceType}LowestPrice`],
      );
    })
    .filter((item) => isNotNullOrUndefined(item)) as Item[];
  return filteredEntries.map((item, index) => {
    return {
      ...item,
      index: index + 1,
    };
  });
}

function mapSupplementaryInsuranceItemOrUndefined(
  supplementaryInsuranceTypes: SupplementaryInsuranceTypes,
  price: number | undefined,
): Item | undefined {
  return isNotNullOrUndefined(price) ? mapSupplementaryInsuranceItem(supplementaryInsuranceTypes, price) : undefined;
}

export function mapSupplementaryInsurancesOfProduct(
  product: SupplementaryInsuranceTypes,
  supplementaryInsurancesData: SupplementaryInsurancesData,
): Item[] {
  switch (product) {
    case 'basic':
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const basic = supplementaryInsurancesData['basic']!;
      return buildBasicItems(basic);
    case 'hospital':
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const hospital = supplementaryInsurancesData['hospital']!;
      return buildHospitalItems(hospital);
    case 'hospitalViva':
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const hospitalViva = supplementaryInsurancesData['hospitalViva']!;
      return buildHospitalVivaItems(hospitalViva);
    case 'complementary':
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const complementary = supplementaryInsurancesData['complementary']!;
      return buildComplementaryItems(complementary);
    case 'outpatient':
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const outpatient = supplementaryInsurancesData['outpatient']!;
      return buildOutpatientItems(outpatient);
    case 'outpatientViva':
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const outpatientViva = supplementaryInsurancesData['outpatientViva']!;
      return buildOutpatientVivaItems(outpatientViva);
    // NOTE: All other supplementary insurances are mapped onto one entry
    default:
      const lowestPrice = supplementaryInsurancesData[`${product}LowestPrice`] || 0;
      return [mapSupplementaryInsuranceVariantItem(product, lowestPrice)];
  }
}

function buildBasicItems(basicInsurance: HospitalOrBasicInsurance): Item[] {
  const items = [
    mapBasicVariantItemOrUndefined(basicInsurance.general?.price, 'BASIC_GENERAL'),
    mapBasicVariantItemOrUndefined(basicInsurance.flex.flex24?.price, 'BASIC_FLEX_24'),
    mapBasicVariantItemOrUndefined(basicInsurance.flex.flex48?.price, 'BASIC_FLEX_48'),
    mapBasicVariantItemOrUndefined(Math.min(...basicInsurance.semiPrivate.map((i) => i.price)), 'BASIC_SEMI_PRIVATE'),
    mapBasicVariantItemOrUndefined(Math.min(...basicInsurance.private.map((i) => i.price)), 'BASIC_PRIVATE'),
  ];
  return items
    .filter((item) => isNotNullOrUndefined(item))
    .map((item, index) => ({ ...item, index: index + 1 })) as Item[];
}

function buildComplementaryItems(complementary: Complementary[]): Item[] {
  return complementary.map((c, index) => mapComplementaryVariantItem(c, index + 1));
}

function buildOutpatientItems(outpatient: Outpatient[]): Item[] {
  return outpatient.map((c, index) => mapOutpatientVariantItem(c, index + 1));
}

function buildOutpatientVivaItems(outpatient: Outpatient[]): Item[] {
  return outpatient.map((c, index) => mapOutpatientVivaVariantItem(c, index + 1));
}

function buildHospitalItems(hospitalInsurance: HospitalOrBasicInsurance): Item[] {
  const items = [
    mapHospitalVariantItemOrUndefined(hospitalInsurance.general?.price, 'HOSPITAL_GENERAL'),
    mapHospitalVariantItemOrUndefined(hospitalInsurance.flex.flex24?.price, 'HOSPITAL_FLEX_24'),
    mapHospitalVariantItemOrUndefined(hospitalInsurance.flex.flex48?.price, 'HOSPITAL_FLEX_48'),
    mapHospitalVariantItemOrUndefined(hospitalInsurance.flex.flex48?.price, 'HOSPITAL_FLEX_PLUS'),
    mapHospitalVariantItemOrUndefined(
      Math.min(...hospitalInsurance.semiPrivate.map((i) => i.price)),
      'HOSPITAL_SEMI_PRIVATE',
    ),
    mapHospitalVariantItemOrUndefined(Math.min(...hospitalInsurance.private.map((i) => i.price)), 'HOSPITAL_PRIVATE'),
  ];
  return items.filter((i) => isNotNullOrUndefined(i)).map((i, index) => ({ ...i, index: index + 1 })) as Item[];
}

function buildHospitalVivaItems(hospitalInsurance: HospitalOrBasicInsurance): Item[] {
  const items = [
    mapHospitalVivaVariantItemOrUndefined(hospitalInsurance.general?.price, 'HOSPITAL_VIVA_GENERAL'),
    mapHospitalVivaVariantItemOrUndefined(hospitalInsurance.flex.flex24?.price, 'HOSPITAL_VIVA_FLEX_24'),
    mapHospitalVivaVariantItemOrUndefined(hospitalInsurance.flex.flex48?.price, 'HOSPITAL_VIVA_FLEX_48'),
    mapHospitalVivaVariantItemOrUndefined(
      Math.min(...hospitalInsurance.semiPrivate.map((i) => i.price)),
      'HOSPITAL_VIVA_SEMI_PRIVATE',
    ),
    mapHospitalVivaVariantItemOrUndefined(
      Math.min(...hospitalInsurance.private.map((i) => i.price)),
      'HOSPITAL_VIVA_PRIVATE',
    ),
  ];
  return items.filter((i) => isNotNullOrUndefined(i)).map((i, index) => ({ ...i, index: index + 1 })) as Item[];
}

export function mapBasicSupplementaryInsuranceDetailItem(
  offerType: BasicOfferType,
  totalPersons: number,
  gender: GenderOrPrenatal,
  price: number,
  contractDuration?: ContractDuration,
  purchaseType?: string,
): Item {
  return mapDetailItem(
    basicId[offerType],
    supplementaryInsuranceNames['basic'],
    basicVariant[offerType],
    price,
    gender,
    totalPersons,
    contractDuration,
    purchaseType,
  );
}

export function mapOutpatientSupplementaryInsuranceDetailItem(
  level: Level,
  totalPersons: number,
  gender: GenderOrPrenatal,
  price: number,
  contractDuration?: ContractDuration,
  purchaseType?: string,
): Item {
  return mapDetailItem(
    outpatientId[level],
    supplementaryInsuranceNames['outpatient'],
    outpatientVariant[level],
    price,
    gender,
    totalPersons,
    contractDuration,
    purchaseType,
  );
}

export function mapOutpatientVivaSupplementaryInsuranceDetailItem(
  level: Level,
  totalPersons: number,
  gender: GenderOrPrenatal,
  price: number,
  contractDuration?: ContractDuration,
  purchaseType?: string,
): Item {
  return mapDetailItem(
    outpatientVivaId[level],
    supplementaryInsuranceNames['outpatientViva'],
    outpatientVariant[level],
    price,
    gender,
    totalPersons,
    contractDuration,
    purchaseType,
  );
}

export function mapComplementarySupplementaryInsuranceDetailItem(
  level: Level,
  totalPersons: number,
  gender: GenderOrPrenatal,
  price: number,
  contractDuration?: ContractDuration,
  purchaseType?: string,
): Item {
  return mapDetailItem(
    complementaryId[level],
    supplementaryInsuranceNames['complementary'],
    complementaryVariant[level],
    price,
    gender,
    totalPersons,
    contractDuration,
    purchaseType,
  );
}

export function mapHospitalSupplementaryInsuranceDetailItem(
  offerType: HospitalOfferType,
  totalPersons: number,
  gender: GenderOrPrenatal,
  price: number,
  contractDuration?: ContractDuration,
  purchaseType?: string,
): Item {
  return mapDetailItem(
    hospitalId[offerType],
    supplementaryInsuranceNames['hospital'],
    hospitalVariant[offerType],
    price,
    gender,
    totalPersons,
    contractDuration,
    purchaseType,
  );
}

export function mapHospitalVivaSupplementaryInsuranceDetailItem(
  offerType: HospitalVivaOfferType,
  totalPersons: number,
  gender: GenderOrPrenatal,
  price: number,
  contractDuration?: ContractDuration,
  purchaseType?: string,
): Item {
  return mapDetailItem(
    hospitalVivaId[offerType],
    supplementaryInsuranceNames['hospitalViva'],
    hospitalVivaVariant[offerType],
    price,
    gender,
    totalPersons,
    contractDuration,
    purchaseType,
  );
}

export function mapSecondarySupplementaryInsuranceDetailItem(
  supplementaryInsuranceType: SupplementaryInsuranceTypes,
  totalPersons: number,
  gender: GenderOrPrenatal,
  price: number,
  contractDuration?: ContractDuration,
  purchaseType?: string,
): Item {
  const insuranceName = supplementaryInsuranceNames[supplementaryInsuranceType];
  return mapDetailItem(
    supplementaryInsuranceIds[supplementaryInsuranceType],
    insuranceName,
    insuranceName,
    price,
    gender,
    totalPersons,
    contractDuration,
    purchaseType,
  );
}

function mapDetailItem(
  itemId: string,
  itemName: string,
  itemVariant: string,
  price: number,
  gender: GenderOrPrenatal,
  totalPersons: number,
  contractDuration?: ContractDuration,
  purchaseType?: string,
): Item {
  return {
    item_id: itemId,
    item_name: itemName,
    currency: CURRENCY_CHF,
    index: 1,
    item_category: CATEGORY.HEALTH_INSURANCE,
    item_category2: CATEGORY.SUPPLEMENTARY_INSURANCE,
    item_list_id: CATEGORY_ID.SUPPLEMENTARY_INSURANCE,
    item_list_name: CATEGORY_ID.SUPPLEMENTARY_INSURANCE,
    item_variant: itemVariant,
    price: price,
    quantity: 1,
    gender: genderNames[gender],
    customer_type: mapCustomerType(totalPersons),
    contract_length: contractDuration ? mapContractLength(contractDuration) : undefined,
    purchase_type: purchaseType,
  };
}
